import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import PharmatechLayout from "../components/PharmatechLayout";
import { Search, Printer, User, Phone, Calendar, Pill, ClipboardList, Loader2, FileText } from "lucide-react";

const STATUS_BG = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  orange: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
};

function formatAge(ageMois) {
  if (!ageMois && ageMois !== 0) return null;
  if (ageMois < 12) return `${ageMois} mois`;
  if (ageMois < 36) return `${Math.floor(ageMois / 12)}a ${ageMois % 12}m`;
  if (ageMois < 216) return `${Math.floor(ageMois / 12)} ans`;
  return `${Math.floor(ageMois / 12)} ans`;
}

export default function ConseilHistoryPage() {
  const { token } = useAuth();
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (s = "") => {
    setLoading(true);
    try {
      const params = s ? { search: s, limit: 100 } : { limit: 100 };
      const r = await axios.get(`${API}/prescription-scanner/conseil-history`, { headers, params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Erreur de chargement de l'historique");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(""); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search.trim());
  };

  const handleReprint = async (checkId) => {
    setPrinting(checkId);
    try {
      const res = await fetch(`${API}/prescription-scanner/conseil-express/${checkId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error("Fiche introuvable");
        return;
      }
      const html = await res.text();
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Activez les popups pour imprimer");
      else toast.success("Fiche prête à imprimer");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error("Erreur lors de la réimpression");
    } finally {
      setPrinting(null);
    }
  };

  return (
    <PharmatechLayout title="Historique Fiches Conseil" subtitle="Recherche par nom ou téléphone du patient — réimpression sur demande">
      <div className="space-y-4" data-testid="conseil-history-page">
        {/* Bandeau intro + recherche */}
        <div className="bg-gradient-to-br from-emerald-700 to-teal-800 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center gap-3 mb-3">
            <ClipboardList className="w-6 h-6 text-amber-300" />
            <div>
              <h2 className="text-lg font-bold">Historique des Fiches Conseil imprimées</h2>
              <p className="text-xs text-emerald-100/90">
                Retrouvez et réimprimez instantanément toute fiche conseil délivrée — utile pour le suivi patient et la traçabilité officinale.
              </p>
            </div>
          </div>
          <form onSubmit={handleSearch} className="flex gap-2 mt-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-emerald-300/80" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom ou téléphone du patient (ex: Aminata, +22507...)"
                data-testid="conseil-history-search-input"
                className="w-full pl-10 pr-3 py-2.5 rounded-lg text-sm bg-white/95 text-slate-800 border border-emerald-200/30 focus:ring-2 focus:ring-amber-300 focus:bg-white outline-none placeholder:text-slate-400"
              />
            </div>
            <button
              type="submit"
              data-testid="conseil-history-search-btn"
              className="px-5 py-2.5 rounded-lg bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold text-sm transition shadow"
            >
              Rechercher
            </button>
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(""); load(""); }}
                className="px-3 py-2.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm transition"
              >
                Effacer
              </button>
            )}
          </form>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs text-slate-500 px-1">
          <span data-testid="conseil-history-count">
            {loading ? "Chargement…" : `${total} fiche${total > 1 ? "s" : ""}`}
            {search && ` correspondant à « ${search} »`}
          </span>
          <span className="text-slate-400">Triées par date décroissante</span>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {search ? "Aucune fiche ne correspond à votre recherche." : "Aucune fiche conseil enregistrée pour le moment."}
            </p>
            {!search && (
              <p className="text-xs text-slate-400 mt-2">
                Les fiches conseil créées via la « Vérification Rapide » apparaîtront ici.
              </p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid="conseil-history-list">
            <div className="divide-y divide-slate-100">
              {items.map((it) => {
                const colorClass = STATUS_BG[it.statut_color] || "bg-slate-50 text-slate-700 border-slate-200";
                const created = it.created_at ? new Date(it.created_at) : null;
                const ageStr = formatAge(it.patient_age_mois);
                return (
                  <div
                    key={it.check_id}
                    className="p-4 hover:bg-slate-50 transition flex flex-wrap items-center gap-4"
                    data-testid={`conseil-row-${it.check_id}`}
                  >
                    {/* Patient */}
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="font-bold text-slate-800 text-sm truncate">
                          {it.patient_name || <span className="text-slate-400 italic font-normal">Patient anonyme</span>}
                        </span>
                      </div>
                      {it.patient_phone && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{it.patient_phone}</span>
                        </div>
                      )}
                      {(ageStr || it.patient_poids_kg) && (
                        <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-1">
                          {ageStr && <span>{ageStr}</span>}
                          {it.patient_poids_kg && <span>· {it.patient_poids_kg} kg</span>}
                          {(it.statut_clinique || []).map(s => (
                            <span key={s} className="px-1.5 py-0.5 rounded bg-pink-50 text-pink-600 font-semibold uppercase tracking-wide text-[9px]">{s.replace("_", " ")}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Médicaments */}
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 text-xs text-slate-700">
                        <Pill className="w-3.5 h-3.5 text-amber-500" />
                        <span className="font-semibold">{it.nb_medicaments} médicament{it.nb_medicaments > 1 ? "s" : ""}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 truncate" title={it.medicaments_summary}>
                        {it.medicaments_summary}
                      </div>
                    </div>

                    {/* Statut */}
                    <div className="flex flex-col items-start gap-1 min-w-[120px]">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${colorClass}`}>
                        {it.statut_label || "—"}
                      </span>
                      {created && (
                        <span className="flex items-center gap-1 text-[11px] text-slate-400">
                          <Calendar className="w-3 h-3" />
                          {created.toLocaleDateString("fr-FR")} · {created.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </div>

                    {/* Action */}
                    <button
                      onClick={() => handleReprint(it.check_id)}
                      disabled={printing === it.check_id}
                      data-testid={`reprint-btn-${it.check_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-xs font-semibold transition shadow-sm"
                    >
                      {printing === it.check_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Printer className="w-3.5 h-3.5" />
                      )}
                      Réimprimer
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer info */}
        <div className="text-[11px] text-slate-400 text-center px-2 py-1">
          Service inclus dans votre formule Pharma Dispensation · Aucune facturation au patient
        </div>
      </div>
    </PharmatechLayout>
  );
}
